/**
 * Mass Email Composer — sends 1-by-1 personalized emails via the user's Gmail.
 *
 * Strict contract:
 *   - DRAFT-ONLY until user clicks final "Send to N contacts" button.
 *   - Each recipient receives an INDIVIDUAL message (never CC/BCC).
 *   - Tokens replaced PER recipient before send.
 *   - Rep MUST see preview of first email before confirm.
 *
 * Two integration modes:
 *   - Standalone (used from Email Designer "Multiple recipients" toggle)
 *   - opportunityId mode (used from AI Outreach Composer; pre-loads contacts
 *     associated with the opportunity)
 *
 * Usage:
 *   <MassEmailComposer
 *     open={open} onOpenChange={setOpen}
 *     initialSubject={subject} initialBody={body}
 *     opportunityId={opportunity?.id}     // optional — pre-loads opp contacts
 *   />
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Users, Send, Eye, AlertTriangle, Loader2, CheckCircle2, XCircle, Search,
  Sparkles, X, Mail, Clock, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';


const TOKENS = ['{first_name}', '{last_name}', '{full_name}', '{company}', '{contact_title}', '{email}'];


function buildRecipientFromContact(c) {
  return {
    email: c.email || '',
    name: c.name || '',
    company: c.company || '',
    contact_title: c.title || c.role || '',
    contact_id: c.id,
  };
}


export default function MassEmailComposer({
  open, onOpenChange,
  initialSubject = '',
  initialBody = '',
  opportunityId = null,
  lockedRecipient = null,        // when set, hides contact picker and locks single send
  lockedTitle = null,            // optional dialog title override
  prefilledRecipients = null,    // when set, locks bulk send to this exact list
}) {
  const api = useApi();
  const { token } = useAuth();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  // form state
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [contacts, setContacts] = useState([]);                 // all available
  const [selectedIds, setSelectedIds] = useState(new Set());    // id set
  const [tagFilter, setTagFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(true);

  // preview + send state
  const [usage, setUsage] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);

  // schedule state
  const [mode, setMode] = useState('now'); // 'now' | 'schedule'
  const [scheduleAt, setScheduleAt] = useState(''); // local datetime-input value
  const [scheduling, setScheduling] = useState(false);
  const [scheduleResult, setScheduleResult] = useState(null);

  // Reset whenever dialog reopens
  useEffect(() => {
    if (!open) return;
    setSubject(initialSubject);
    setBody(initialBody);
    setSelectedIds(new Set());
    setSearch('');
    setTagFilter('all');
    setPreviewData(null);
    setResult(null);
    setScheduleResult(null);
    setMode('now');
    setScheduleAt('');
    setProgress({ done: 0, total: 0 });
  }, [open, initialSubject, initialBody]);

  // Load contacts + usage
  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoadingContacts(true);
      try {
        const [contactsRes, usageRes] = await Promise.all([
          api.get('/contacts'),
          api.get('/gmail/mass/usage'),
        ]);
        setContacts(Array.isArray(contactsRes) ? contactsRes : (contactsRes?.contacts || []));
        setUsage(usageRes);
      } catch (e) {
        toast.error(e?.message || 'Failed to load contacts');
      } finally {
        setLoadingContacts(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When opportunity-mode, auto-select contacts linked to the opportunity
  useEffect(() => {
    if (!open || !opportunityId || contacts.length === 0) return;
    const linked = contacts.filter(
      (c) => Array.isArray(c.opportunity_ids) && c.opportunity_ids.includes(opportunityId),
    );
    if (linked.length > 0) {
      setSelectedIds(new Set(linked.map((c) => c.id)));
    }
  }, [open, opportunityId, contacts]);

  // ─────────── derived ───────────

  const allTags = useMemo(() => {
    const s = new Set();
    contacts.forEach((c) => (c.contact_type_tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [contacts]);

  const visibleContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (!c.email) return false; // can't email without an address
      if (tagFilter !== 'all' && !(c.contact_type_tags || []).includes(tagFilter)) return false;
      if (q) {
        const blob = `${c.name || ''} ${c.email || ''} ${c.company || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [contacts, search, tagFilter]);

  const selectedContacts = useMemo(
    () => contacts.filter((c) => selectedIds.has(c.id) && c.email),
    [contacts, selectedIds],
  );

  const recipients = useMemo(() => {
    if (lockedRecipient) return [lockedRecipient];
    if (prefilledRecipients && prefilledRecipients.length > 0) return prefilledRecipients;
    return selectedContacts.map(buildRecipientFromContact);
  }, [lockedRecipient, prefilledRecipients, selectedContacts]);

  const wouldExceedHard = usage && (usage.count + recipients.length > usage.hard_cap);
  const hardBlocked = usage && usage.hard_block;

  // ─────────── handlers ───────────

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleContacts.forEach((c) => next.add(c.id));
      return next;
    });
  };

  const clearAll = () => setSelectedIds(new Set());

  const insertToken = (tok) => setBody((prev) => `${prev}${tok}`);

  const handlePreview = async () => {
    if (recipients.length === 0) { toast.error('Pick at least one recipient'); return; }
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body are required'); return; }
    setPreviewing(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/gmail/mass/preview`,
        {
          subject_template: subject,
          body_template: body,
          recipient: recipients[0],
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreviewData(res.data);
      setConfirming(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Preview failed');
      toast.error(msg);
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmSend = async () => {
    setSending(true);
    setProgress({ done: 0, total: recipients.length });
    try {
      const res = await axios.post(
        `${BACKEND}/api/gmail/mass/send-batch`,
        {
          subject_template: subject,
          body_template: body,
          recipients,
          opportunity_id: opportunityId || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 + recipients.length * 1500 },
      );
      setResult(res.data);
      setConfirming(false);
      if (res.data.failed > 0) {
        toast.warning(`Sent ${res.data.sent}, ${res.data.failed} failed`);
      } else {
        toast.success(`Sent ${res.data.sent} email${res.data.sent === 1 ? '' : 's'}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Send failed');
      toast.error(msg);
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  const handleConfirmSchedule = async () => {
    if (!scheduleAt) { toast.error('Pick a send time'); return; }
    // Convert local datetime-local → ISO UTC
    const local = new Date(scheduleAt);
    if (isNaN(local.getTime())) { toast.error('Invalid date'); return; }
    const sendAtIso = local.toISOString();

    setScheduling(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/gmail/mass/schedule`,
        {
          subject_template: subject,
          body_template: body,
          recipients,
          send_at: sendAtIso,
          opportunity_id: opportunityId || undefined,
          timezone_label: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setScheduleResult(res.data);
      setConfirming(false);
      toast.success('Batch scheduled');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Schedule failed');
      toast.error(msg);
    } finally {
      setScheduling(false);
    }
  };

  // ─────────── render ───────────

  if (scheduleResult) {
    const sendAtLocal = new Date(scheduleResult.send_at).toLocaleString();
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="mass-email-schedule-result">
          <DialogHeader>
            <DialogTitle>Batch scheduled</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <CalendarClock className="w-8 h-8 text-[#B89B5E]" />
              <div>
                <div className="font-semibold text-lg">{scheduleResult.recipient_count} recipient{scheduleResult.recipient_count === 1 ? '' : 's'}</div>
                <div className="text-muted-foreground">Sends at <span className="font-mono">{sendAtLocal}</span></div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground border-t pt-2">
              You can cancel any time before {scheduleResult.cancel_cutoff_minutes} minutes from send_at.
              Manage from Workspace Settings → Scheduled Emails.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="mass-email-schedule-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (result) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="mass-email-result">
          <DialogHeader>
            <DialogTitle>Batch complete</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              <div>
                <div className="font-semibold text-lg">{result.sent} sent</div>
                {result.failed > 0 && (
                  <div className="text-red-700">{result.failed} failed</div>
                )}
              </div>
            </div>
            {result.failed_recipients && result.failed_recipients.length > 0 && (
              <div className="rounded-sm border bg-red-50 p-3 text-xs space-y-1 max-h-40 overflow-y-auto">
                {result.failed_recipients.map((f, i) => (
                  <div key={i} className="text-red-800">
                    <span className="font-mono">{f.email}</span> — {f.error}
                  </div>
                ))}
              </div>
            )}
            <div className="text-xs text-muted-foreground border-t pt-2">
              Today's Gmail usage: <span className="font-mono">{result.daily_count} / 500</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="mass-email-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (confirming && previewData) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!sending) onOpenChange(v); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="mass-email-confirm">
          <DialogHeader>
            <DialogTitle>Confirm send</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-sm border bg-muted/20 p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                Preview · first recipient
              </div>
              <div className="text-xs"><span className="font-semibold">To:</span> {previewData.to}</div>
              <div className="text-xs"><span className="font-semibold">Subject:</span> {previewData.subject}</div>
              <div
                className="mt-2 prose prose-sm max-w-none border-t pt-2 max-h-64 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: previewData.body }}
                data-testid="mass-email-preview-body"
              />
            </div>
            <div className="text-xs text-muted-foreground">
              Each recipient receives their own personalized email. Sent 1-by-1 with a 1-second delay.
            </div>
            {sending && (
              <div className="flex items-center gap-2 text-sm text-[#B89B5E]">
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending {progress.done} of {progress.total}…
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline" onClick={() => setConfirming(false)}
              disabled={sending || scheduling} className="rounded-sm"
              data-testid="mass-email-confirm-back"
            >
              Back to edit
            </Button>
            {mode === 'schedule' ? (
              <Button
                onClick={handleConfirmSchedule} disabled={scheduling}
                className="rounded-sm" style={{ backgroundColor: '#B89B5E', color: 'white' }}
                data-testid="mass-email-confirm-schedule"
              >
                {scheduling
                  ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Scheduling…</>)
                  : (<><CalendarClock className="w-4 h-4 mr-1.5" /> Schedule batch for {scheduleAt ? new Date(scheduleAt).toLocaleString() : '—'}</>)
                }
              </Button>
            ) : (
              <Button
                onClick={handleConfirmSend} disabled={sending}
                className="rounded-sm" style={{ backgroundColor: '#B89B5E', color: 'white' }}
                data-testid="mass-email-confirm-send"
              >
                {sending
                  ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending {progress.total}…</>)
                  : (<><Send className="w-4 h-4 mr-1.5" /> Send to all {recipients.length} contacts</>)}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mass-email-composer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {lockedRecipient
              ? <Mail className="w-5 h-5 text-[#B89B5E]" />
              : <Users className="w-5 h-5 text-[#B89B5E]" />}
            {lockedTitle || (lockedRecipient
              ? `Send Email · ${lockedRecipient.name || lockedRecipient.email}`
              : (prefilledRecipients
                  ? `Mass Email · ${prefilledRecipients.length} selected contacts`
                  : 'Mass Email · personalized via your Gmail'))}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Usage banner */}
          {usage && (usage.soft_warn || usage.hard_block) && (
            <div className={`rounded-sm border px-3 py-2 text-xs flex items-start gap-2 ${
              usage.hard_block ? 'border-red-300 bg-red-50 text-red-900' : 'border-amber-300 bg-amber-50 text-amber-900'
            }`} data-testid="mass-email-usage-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">
                  {usage.hard_block ? 'Daily Gmail limit reached' : `Approaching daily limit (${usage.count}/500)`}
                </span>
                {' · '}
                {usage.hard_block
                  ? 'Try again tomorrow.'
                  : `${usage.hard_cap - usage.count} sends remaining today.`}
              </div>
            </div>
          )}

          {/* Subject + body editor */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Subject template</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Quick note for {first_name} at {company}"
                className="rounded-sm"
                data-testid="mass-email-subject"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Body template</Label>
                <div className="flex flex-wrap gap-1">
                  {TOKENS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => insertToken(t)}
                      className="text-[10px] font-mono bg-[#B89B5E]/10 text-[#B89B5E] hover:bg-[#B89B5E]/20 rounded-sm px-1.5 py-0.5"
                      data-testid={`mass-email-token-${t.replace(/[{}]/g, '')}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <Textarea
                rows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Hi {first_name},&#10;&#10;A quick note about {company}…"
                className="rounded-sm"
                data-testid="mass-email-body"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Tokens are replaced per recipient. Unknown tokens stay as written.
              </div>
            </div>
          </div>

          {/* Mode toggle — Send now vs Schedule */}
          <div className="flex items-center justify-between gap-2 p-3 rounded-sm border bg-muted/20" data-testid="mass-email-mode-toggle">
            <div className="flex items-center gap-2 text-sm">
              {mode === 'schedule' ? <CalendarClock className="w-4 h-4 text-[#B89B5E]" /> : <Send className="w-4 h-4 text-[#B89B5E]" />}
              <span>
                {mode === 'schedule'
                  ? (scheduleAt
                      ? `Scheduled for ${new Date(scheduleAt).toLocaleString()}`
                      : 'Pick a send time below')
                  : 'Sends immediately on confirm'}
              </span>
            </div>
            <div className="flex gap-1">
              <Button
                type="button" size="sm"
                variant={mode === 'now' ? 'default' : 'outline'}
                onClick={() => setMode('now')}
                className="rounded-sm h-8"
                data-testid="mass-email-mode-now"
              >
                <Send className="w-3.5 h-3.5 mr-1" /> Send now
              </Button>
              <Button
                type="button" size="sm"
                variant={mode === 'schedule' ? 'default' : 'outline'}
                onClick={() => setMode('schedule')}
                className="rounded-sm h-8"
                data-testid="mass-email-mode-schedule"
              >
                <Clock className="w-3.5 h-3.5 mr-1" /> Schedule
              </Button>
            </div>
          </div>

          {mode === 'schedule' && (
            <div className="rounded-sm border p-3 space-y-2" data-testid="mass-email-schedule-input-wrap">
              <Label className="text-xs">Send at (your local time)</Label>
              <Input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                min={(() => {
                  const d = new Date(Date.now() + 6 * 60 * 1000); // min = now + 6 min
                  d.setSeconds(0, 0);
                  return d.toISOString().slice(0, 16);
                })()}
                className="rounded-sm"
                data-testid="mass-email-schedule-input"
              />
              <div className="text-[10px] text-muted-foreground">
                Must be at least 5 minutes from now. Cancel any time up to 5 minutes before send.
              </div>
            </div>
          )}

          {/* Contact picker — hidden when sending to a single locked recipient OR pre-filled bulk list */}
          {!lockedRecipient && !prefilledRecipients && (
          <div className="rounded-sm border p-3 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#B89B5E]" />
                <span className="text-sm font-semibold">Recipients</span>
                <Badge className="rounded-sm bg-[#B89B5E] hover:bg-[#B89B5E] text-white" data-testid="mass-email-count">
                  Sending to {selectedIds.size} contact{selectedIds.size === 1 ? '' : 's'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={selectAllVisible} data-testid="mass-email-select-all">
                  Select all visible
                </Button>
                <Button size="sm" variant="ghost" className="h-7 rounded-sm text-xs" onClick={clearAll} data-testid="mass-email-clear">
                  Clear
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, company…"
                  className="pl-8 rounded-sm h-8 text-sm"
                  data-testid="mass-email-search"
                />
              </div>
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="rounded-sm h-8 w-[180px] text-sm" data-testid="mass-email-tag-filter">
                  <SelectValue placeholder="Filter by tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tags</SelectItem>
                  {allTags.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border rounded-sm max-h-60 overflow-y-auto" data-testid="mass-email-contact-list">
              {loadingContacts ? (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading contacts…
                </div>
              ) : visibleContacts.length === 0 ? (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  No contacts match these filters.
                </div>
              ) : (
                visibleContacts.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/40 cursor-pointer text-sm border-b last:border-b-0"
                    data-testid={`mass-email-contact-row-${c.id}`}
                  >
                    <Checkbox
                      checked={selectedIds.has(c.id)}
                      onCheckedChange={() => toggleId(c.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name || c.email}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {c.email}{c.company ? ` · ${c.company}` : ''}
                      </div>
                    </div>
                    {(c.contact_type_tags || []).slice(0, 2).map((t) => (
                      <Badge key={t} variant="outline" className="rounded-sm text-[9px]">{t}</Badge>
                    ))}
                  </label>
                ))
              )}
            </div>
          </div>
          )}

          {/* Locked-recipient banner — replaces the contact picker for single-send */}
          {lockedRecipient && (
            <div className="rounded-sm border p-3 bg-muted/20" data-testid="mass-email-locked-recipient">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Recipient</div>
              <div className="font-medium">{lockedRecipient.name || lockedRecipient.email}</div>
              <div className="text-xs text-muted-foreground font-mono">{lockedRecipient.email}</div>
              {(lockedRecipient.company || lockedRecipient.contact_title) && (
                <div className="text-xs text-muted-foreground mt-0.5">
                  {[lockedRecipient.contact_title, lockedRecipient.company].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          )}

          {/* Pre-filled bulk recipients banner — for "Send mass email" from contacts list */}
          {prefilledRecipients && prefilledRecipients.length > 0 && (
            <div className="rounded-sm border p-3 bg-muted/20" data-testid="mass-email-prefilled-recipients">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Sending to {prefilledRecipients.length} contact{prefilledRecipients.length === 1 ? '' : 's'}
                </div>
                <Badge className="rounded-sm bg-[#B89B5E] hover:bg-[#B89B5E] text-white">
                  Bulk
                </Badge>
              </div>
              <div className="max-h-28 overflow-y-auto space-y-1 text-xs font-mono text-muted-foreground">
                {prefilledRecipients.slice(0, 8).map((r, i) => (
                  <div key={i} className="truncate">{r.name ? `${r.name} · ` : ''}{r.email}</div>
                ))}
                {prefilledRecipients.length > 8 && (
                  <div className="italic">+ {prefilledRecipients.length - 8} more…</div>
                )}
              </div>
            </div>
          )}

          {wouldExceedHard && (
            <div className="rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900 flex items-start gap-2">
              <XCircle className="w-4 h-4 mt-0.5" />
              <div>
                <div className="font-semibold">This batch would exceed your daily Gmail limit</div>
                <div>
                  {usage.hard_cap - usage.count} sends remaining today · select {Math.max(0, usage.hard_cap - usage.count)} contacts or fewer.
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button
            onClick={handlePreview}
            disabled={previewing || hardBlocked || wouldExceedHard || recipients.length === 0 || !subject.trim() || !body.trim() || (mode === 'schedule' && !scheduleAt)}
            className="rounded-sm"
            data-testid="mass-email-preview-btn"
          >
            {previewing
              ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Loading preview…</>)
              : (<><Eye className="w-4 h-4 mr-1.5" /> {mode === 'schedule' ? 'Preview & schedule' : 'Preview & confirm'}</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
